import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import planeIcon from '../assets/plane.svg';

const FlightRefCard = ({ flight, price, points, cpp, addedDate, loyaltyCurrency }) => {
  const [showDetails, setShowDetails] = useState(false);

  // Early return if flight is not provided
  if (!flight) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-500">Flight data not available</p>
      </div>
    );
  }

  // Format travel date
  const formatTravelDate = (date) => {
    if (!date) return 'Date not set';
    const travelDate = new Date(date);
    return travelDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format added date
  const formatAddedDate = (date) => {
    if (!date) return 'Date not set';
    const added = new Date(date);
    const month = added.toLocaleDateString('en-US', { month: 'short' });
    const day = added.getDate();
    const year = added.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  // Get route display
  const getRouteDisplay = () => {
    const segments = flight?.segments || [];
    if (segments.length === 1) {
      // One-way flight
              return (segments[0]?.origin_iata || 'LAX') + ' - ' + (segments[0]?.destination_iata || 'JFK');
    } else if (segments.length === 2 && flight?.trip_type === 'round_trip') {
      // Round trip: show first and last airports
      const first = segments[0];
      const last = segments[segments.length - 1];
      if (first && last) {
        return (first.origin_iata || 'LAX') + ' - ... - ' + (last.destination_iata || 'JFK');
      }
    } else if (segments.length > 1 && flight?.trip_type === 'multi_city') {
      // Multi-city: show first and last airports
      const first = segments[0];
      const last = segments[segments.length - 1];
      if (first && last) {
        return (first.origin_iata || 'LAX') + ' - ... - ' + (last.destination_iata || 'JFK');
      }
    }
    return 'Route not set';
  };

  // Get date range
  const getDateRange = () => {
    const segments = flight?.segments || [];
    if (segments.length === 1) {
      // One-way flight
      return formatTravelDate(segments[0]?.travel_date);
    } else if (segments.length > 1) {
      // Round trip or multi-city: show first and last dates
      const first = segments[0];
      const last = segments[segments.length - 1];
      if (first?.travel_date && last?.travel_date) {
        const firstDate = new Date(first.travel_date);
        const lastDate = new Date(last.travel_date);
        const firstMonth = firstDate.toLocaleDateString('en-US', { month: 'short' });
        const firstDay = firstDate.getDate();
        const lastMonth = lastDate.toLocaleDateString('en-US', { month: 'short' });
        const lastDay = lastDate.getDate();
        const year = lastDate.getFullYear();
        
        if (firstMonth === lastMonth) {
          return `${firstMonth} ${firstDay} - ${lastDay}, ${year}`;
        } else {
                      return `${firstMonth} ${firstDay} - ${lastMonth} ${lastDay}, ${year}`;
        }
      }
    }
    return 'Date not set';
  };

  // Get flight details
  const getFlightDetails = () => {
    const segments = flight?.segments || [];
    if (segments.length === 1) {
      // One-way flight
      const segment = segments[0];
      const carrier = segment?.carrier?.iata_code || 'UA';
      const flightNumber = segment?.flight_number;
      const cabinClass = segment?.cabin_class || 'Economy';
      
      if (flightNumber) {
        return `${cabinClass} • ${carrier} ${flightNumber}`;
      } else {
        return cabinClass;
      }
    } else if (segments.length > 1) {
      // Round trip or multi-city: show first segment details
      const firstSegment = segments[0];
      const carrier = firstSegment?.carrier?.iata_code || 'UA';
      const flightNumber = firstSegment?.flight_number;
      const cabinClass = firstSegment?.cabin_class || 'Economy';
      
      if (flightNumber) {
        return `${cabinClass} • ${carrier} ${flightNumber} - ...`;
      } else {
        return `${cabinClass} - ...`;
      }
    }
    return 'Flight details not set';
  };

  // Check if flight has multiple segments (round-trip or multi-city)
  const hasMultipleSegments = (flight?.segments || []).length > 1;

  const routeDisplay = getRouteDisplay();
  const dateRange = getDateRange();
  const flightDetails = getFlightDetails();
  const addedDateFormatted = formatAddedDate(addedDate);

  return (
    <div className="relative h-full flex flex-col">
      {/* Main card */}
      <div className="bg-white shadow-md rounded-lg p-4 flex flex-col h-full">
        {/* Top section */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            {/* Flight icon */}
            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <img src={planeIcon} alt="Flight" className="w-8 h-8" />
            </div>
            
            {/* Route and date */}
            <div className="min-w-0 flex-1">
              <p className="text-gray-800 text-base leading-tight line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>{routeDisplay}</p>
              <p className="text-sm mt-2" style={{ fontFamily: 'Inter, sans-serif', color: '#A8A8A8' }}>{dateRange}</p>
            </div>
          </div>
          
          {/* Price */}
          <div className="text-right flex-shrink-0 ml-2">
            <p className="text-base font-medium" style={{ fontFamily: 'Inter, sans-serif', color: '#A8A8A8' }}>
              ${price ? parseFloat(price).toFixed(0) : '0'}
            </p>
          </div>
        </div>
        
        {/* Flight details with View More option */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-gray-700 text-sm line-clamp-1" style={{ fontFamily: 'Inter, sans-serif' }}>{flightDetails}</p>
          {hasMultipleSegments && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs flex items-center space-x-1 flex-shrink-0 ml-2"
              style={{ color: '#9F9F9F', fontFamily: 'Inter, sans-serif' }}
            >
              <span>View More</span>
              {showDetails ? (
                <ChevronUpIcon className="w-3 h-3" />
              ) : (
                <ChevronDownIcon className="w-3 h-3" />
              )}
            </button>
          )}
        </div>
        
        {/* Expanded details for round-trip and multi-city */}
        {showDetails && hasMultipleSegments && (
          <div className="mb-4 space-y-2">
            {flight.segments.map((segment, index) => (
              <div key={index} className="text-xs text-gray-600 pl-3 border-l-2 border-gray-300">
                <p>
                  {segment.origin_iata} → {segment.destination_iata} • {segment.cabin_class}
                  {segment.carrier?.iata_code && segment.flight_number && 
                    ` • ${segment.carrier.iata_code} ${segment.flight_number}`
                  }
                  {segment.travel_date && ` • ${formatTravelDate(segment.travel_date)}`}
                </p>
              </div>
            ))}
          </div>
        )}
        
        {/* Spacer to push bottom content down */}
        <div className="flex-1"></div>
        
        {/* Bottom section - points and CPP */}
        <div className="flex justify-between items-center pt-2">
          <div>
            <span className="font-bold text-base" style={{ color: '#548BCB', fontFamily: 'Inter, sans-serif' }}>
              {points ? points.toLocaleString() : '0'}
            </span>
            <span className="text-gray-500 text-xs ml-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              {loyaltyCurrency || 'pts'}
            </span>
          </div>
          
          <div>
            <span className="font-bold text-base" style={{ color: '#7395C2', fontFamily: 'Inter, sans-serif' }}>
              {cpp ? cpp.toFixed(2) : '0.0'}
            </span>
            <span className="text-gray-500 text-xs ml-1" style={{ fontFamily: 'Inter, sans-serif' }}>¢</span>
          </div>
        </div>
      </div>
      
      {/* Added date below card */}
      <div className="text-right mt-2">
        <p className="text-gray-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Added: {addedDateFormatted}</p>
      </div>
    </div>
  );
};

export default FlightRefCard;
